import { isMeetingTab, resolveMeetingTab } from '../../views/meeting-view/meeting-tabs'
import type { MeetingViewContext } from '../../views/meeting-view/use-meeting-view-model'

/**
 * The one view id for the Meeting View (HIG-5823). A meeting is a single
 * surface across its whole lifecycle, so it gets a single id; whether an entry
 * follows the live recording or names a specific meeting is `context.mode`,
 * which is a property of the open, not of the destination.
 *
 * Pure module — no atoms, no React — so the predicate below stays testable.
 */
export const MEETING_VIEW_ID = 'meeting'

/** What the recording in progress is for, longest-lived id first. */
export interface LiveMeetingIdentity {
  audioNoteId?: string
  calendarEventId?: string
}

/**
 * Whether an open view is the Meeting View showing the meeting being recorded
 * right now. Mini asks this to decide whether it's redundant (the panel already
 * shows the live meeting) — so it has to match on what the entry points at, not
 * merely on the Meeting View being open, or Mini would hide behind a *past*
 * note opened mid-recording.
 */
export function isLiveMeetingView(view: { id: string; context?: unknown }, live: LiveMeetingIdentity): boolean {
  if (view.id !== MEETING_VIEW_ID) return false
  const context = view.context as MeetingViewContext | undefined
  // Nothing to match on: a `live` entry follows the running session by
  // definition, so before it resolves any ids it is the live meeting.
  if (!context?.audioNoteId && !context?.calendarEventId) return context?.mode === 'live'
  return (
    (!!live.audioNoteId && context.audioNoteId === live.audioNoteId) ||
    (!!live.calendarEventId && context.calendarEventId === live.calendarEventId)
  )
}

/**
 * Whether the open view is the live meeting on its transcript. A second click
 * on the waveform closes only then; any other tab still has to switch.
 */
export function isLiveMeetingShowingTranscript(
  view: { id: string; context?: unknown },
  live: LiveMeetingIdentity,
  storedTab: string | undefined,
): boolean {
  if (!isLiveMeetingView(view, live)) {
    return false
  }
  const context = view.context as MeetingViewContext | undefined
  const initialTab = isMeetingTab(context?.initialTab) ? context.initialTab : undefined
  return (
    resolveMeetingTab({
      stored: storedTab,
      variant: 'live',
      initialTab,
      // A brief pick must not count as the transcript, or View transcript would close over it.
      hasBriefTab: true,
    }) === 'transcript'
  )
}
